import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { hotKeywords, navCategories } from "@/mocks/site";
import { useWatchHistory } from "@/hooks/useWatchHistory";

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { entries } = useWatchHistory();
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const currentPath = `${location.pathname}${location.search}`;

  const goSearch = (keyword: string) => {
    const trimmed = keyword.trim();
    setSearchOpen(false);
    setMenuOpen(false);
    if (!trimmed) {
      navigate("/category");
      return;
    }
    navigate(`/category?q=${encodeURIComponent(trimmed)}`);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    goSearch(query);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-foreground-50/10 bg-background-950/85 backdrop-blur-xl">
      <div className="flex h-16 w-full items-center gap-3 px-4 md:gap-5 md:px-6">
        <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2" aria-label="剧焰短剧首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary-300 via-primary-500 to-accent-600 text-background-950 shadow-lg shadow-primary-950/50">
            <i className="ri-movie-2-line text-xl" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-[17px] font-black tracking-wide text-foreground-50">剧焰短剧</span>
            <span className="mt-0.5 text-[9px] tracking-[0.28em] text-foreground-500">JUYAN DRAMA</span>
          </span>
        </Link>

        <nav aria-label="剧集分类导航" className="hidden items-center gap-1 lg:flex">
          {navCategories.map((item) => {
            const active = item.to === "/" ? location.pathname === "/" : currentPath === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                  active
                    ? "bg-primary-500/15 text-primary-300"
                    : "text-foreground-300 hover:bg-foreground-50/5 hover:text-foreground-50"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex flex-1 items-center justify-end gap-2 md:gap-3">
          <form
            role="search"
            onSubmit={handleSubmit}
            className="relative flex-1 sm:max-w-xs md:max-w-sm"
            onFocus={() => setSearchOpen(true)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setSearchOpen(false);
            }}
          >
            <label htmlFor="site-search" className="sr-only">
              搜索剧名、演员或标签
            </label>
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" aria-hidden="true" />
            </span>
            <input
              id="site-search"
              name="q"
              type="search"
              autoComplete="off"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索剧名 / 演员 / 标签"
              className="h-9 w-full rounded-full border border-foreground-50/15 bg-foreground-50/5 pl-9 pr-9 text-sm text-foreground-100 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400/70 focus:bg-foreground-50/10"
            />
            <button
              type="submit"
              aria-label="搜索"
              className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-500/90 text-background-950 transition-colors hover:bg-primary-400"
            >
              <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
            </button>

            {searchOpen && (
              <div className="absolute left-0 right-0 top-11 z-50 rounded-xl border border-foreground-50/10 bg-background-900/98 p-3 shadow-2xl shadow-background-950/80 backdrop-blur-xl">
                <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-foreground-500">
                  <i className="ri-fire-line text-accent-400" aria-hidden="true" />
                  热搜关键词
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {hotKeywords.map((word) => (
                    <button
                      key={word}
                      type="button"
                      onClick={() => {
                        setQuery(word);
                        goSearch(word);
                      }}
                      className="cursor-pointer whitespace-nowrap rounded-full border border-foreground-50/10 bg-foreground-50/5 px-2.5 py-1 text-xs text-foreground-300 transition-colors hover:border-primary-400/60 hover:text-primary-300"
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </form>

          <Link
            to="/history"
            className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-foreground-50/10 px-2.5 py-1.5 text-xs text-foreground-300 transition-colors hover:border-primary-400/60 hover:text-primary-300 md:px-3 md:text-sm"
            aria-label={`播放历史记录，共 ${entries.length} 条`}
          >
            <i className="ri-history-line text-base" aria-hidden="true" />
            <span className="hidden sm:inline">历史</span>
            {entries.length > 0 && (
              <span className="rounded-full bg-primary-500 px-1.5 text-[10px] font-bold text-background-950">
                {entries.length}
              </span>
            )}
          </Link>

          <button
            type="button"
            aria-label={menuOpen ? "关闭分类菜单" : "打开分类菜单"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-foreground-50/10 text-foreground-200 transition-colors hover:text-primary-300 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} aria-hidden="true" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-foreground-50/10 bg-background-950/98 px-4 py-4 lg:hidden">
          <nav aria-label="移动端分类导航" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {navCategories.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className="cursor-pointer rounded-lg border border-foreground-50/10 bg-foreground-50/[0.03] px-3 py-2 text-sm text-foreground-200 transition-colors hover:border-primary-400/50 hover:text-primary-300"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}